/**
 * Make It Premium on the web.
 *
 * Who is Premium is decided by the functions (RevenueCat + the legacy PHP
 * backend, see functions/src/subscription.ts) and kept in
 * users/{uid}.subscription, which the Home reads live (watchAccount). Buying
 * uses RevenueCat Billing through its Web SDK (@revenuecat/purchases-js),
 * loaded only on the Subscription page, with the Firebase uid as the app user
 * id, the same id the app logs in with, so a purchase anywhere is the same
 * customer everywhere.
 */
import { CURRENCY_OVERRIDE } from "../ui/localPrice";
import { getApp, FUNCTIONS_REGION } from "../firebase";
import type { Offering, Package, Purchases as RcPurchases } from "@revenuecat/purchases-js";

/**
 * RevenueCat Billing public key of the "Make It" project (Web › config). Public by design.
 * `?rcsandbox` (kept for the tab) uses the sandbox key instead: Stripe test mode, for testers
 * (the functions only count sandbox purchases of the SANDBOX_TESTERS).
 */
export const RC_SANDBOX: boolean = (() => {
  try {
    if (new URLSearchParams(location.search).has("rcsandbox")) sessionStorage.setItem("makeit.rcsandbox", "1");
    return !!import.meta.env.VITE_RC_SANDBOX_KEY && sessionStorage.getItem("makeit.rcsandbox") === "1";
  } catch {
    return false;
  }
})();
export const RC_WEB_KEY: string = (RC_SANDBOX ? import.meta.env.VITE_RC_SANDBOX_KEY : import.meta.env.VITE_RC_WEB_KEY) ?? "";
/** The offering with the web packages (current offering when empty). */
export const RC_OFFERING: string = import.meta.env.VITE_RC_OFFERING ?? "";

/** The school licence (Firebase) the user manages or teaches in (functions/src/schools.ts). */
export interface SchoolMembership {
  id: string;
  name: string;
  role: "manager" | "teacher";
  /** ms */
  expiresAt: number;
  managerEmail: string | null;
}

export interface Subscription {
  active: boolean;
  /** "admin": given from the admin panel */
  source: "revenuecat" | "admin" | "school" | "legacy" | null;
  entitlement: string | null;
  productId: string | null;
  store: string | null;
  /** ms, null = does not expire */
  expiresAt: number | null;
  willRenew: boolean | null;
  trial: boolean;
  sandbox: boolean;
  managementUrl: string | null;
  schoolAdminEmail: string | null;
  /** whatever else makes them Premium */
  school: SchoolMembership | null;
  /** ms */
  checkedAt?: number | null;
}

/** The free limits (the functions have their own switch, FREE_LIMITS). */
export const FREE_LIMITS_ON = import.meta.env.VITE_FREE_LIMITS === "on";

/** The free version (help4 / AlertsController.NotSubscribed). */
export const FREE = { projects: 2, classrooms: 1, folders: 1 } as const;
export type LimitKind = keyof typeof FREE;

/** Premium right now, from users/{uid}.subscription. */
export function isPremium(s: Subscription | null | undefined, now = Date.now()): boolean {
  return !!s?.active && (s.expiresAt === null || s.expiresAt > now);
}

/** Before creating: false when the free version does not allow one more. */
export function canCreate(kind: LimitKind, have: number, s: Subscription | null | undefined): boolean {
  return !FREE_LIMITS_ON || have < FREE[kind] || isPremium(s);
}

/** A function error saying the free version does not allow it ("free-limit-projects", "not-subscribed"). */
export function limitOfError(err: unknown): LimitKind | "images" | null {
  const m = /free-limit-(projects|classrooms|folders)|not-subscribed/.exec(String((err as { message?: string })?.message ?? ""));
  if (!m) return null;
  return (m[1] as LimitKind | undefined) ?? "images";
}

/** users/{uid}.subscription once (the editor, which does not watch the account). */
export async function fetchSubscription(uid: string): Promise<Subscription | null> {
  const { getFirestore, doc, getDoc } = await import("firebase/firestore");
  const snap = await getDoc(doc(getFirestore(getApp()), "users", uid));
  return subscriptionOf(snap.get("subscription"));
}

export function subscriptionOf(raw: unknown): Subscription | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const checked = r.checkedAt as { toMillis?: () => number } | number | null | undefined;
  return {
    active: r.active === true,
    source: r.source === "revenuecat" || r.source === "admin" || r.source === "school" || r.source === "legacy" ? r.source : null,
    entitlement: typeof r.entitlement === "string" ? r.entitlement : null,
    productId: typeof r.productId === "string" ? r.productId : null,
    store: typeof r.store === "string" ? r.store : null,
    expiresAt: typeof r.expiresAt === "number" ? r.expiresAt : null,
    willRenew: typeof r.willRenew === "boolean" ? r.willRenew : null,
    trial: r.trial === true,
    sandbox: r.sandbox === true,
    managementUrl: typeof r.managementUrl === "string" ? r.managementUrl : null,
    schoolAdminEmail: typeof r.schoolAdminEmail === "string" && r.schoolAdminEmail ? r.schoolAdminEmail : null,
    school: schoolOf(r.school),
    checkedAt: typeof checked === "number" ? checked : (checked?.toMillis?.() ?? null),
  };
}

function schoolOf(raw: unknown): SchoolMembership | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || (r.role !== "manager" && r.role !== "teacher")) return null;
  return { id: r.id, name: typeof r.name === "string" ? r.name : "", role: r.role, expiresAt: Number(r.expiresAt ?? 0) || 0, managerEmail: typeof r.managerEmail === "string" ? r.managerEmail : null };
}

/** Manages a school licence (the account page's "Manage teachers"). */
export const managesSchool = (s: Subscription | null | undefined) => s?.school?.role === "manager";

/** Asks the functions to look again (RevenueCat + PHP); the result also arrives through watchAccount. */
export async function refreshSubscription(): Promise<Subscription | null> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const call = httpsCallable<{ action: "subscription" }, { subscription: unknown }>(getFunctions(getApp(), FUNCTIONS_REGION), "manageAccount", { timeout: 30_000 });
  return subscriptionOf((await call({ action: "subscription" })).data.subscription);
}

// ---------------------------------------------------------------------------
// RevenueCat Web SDK
// ---------------------------------------------------------------------------

let sdk: Promise<{ purchases: RcPurchases; mod: typeof import("@revenuecat/purchases-js") }> | null = null;
let sdkUser = "";

/** The SDK, configured once for this user. */
export function revenueCat(uid: string) {
  if (!RC_WEB_KEY) return Promise.reject(new Error("RevenueCat web key not configured"));
  if (sdk && sdkUser === uid) return sdk;
  sdkUser = uid;
  sdk = import("@revenuecat/purchases-js").then(async (mod) => {
    const P = mod.Purchases;
    if (P.isConfigured()) {
      const p = P.getSharedInstance();
      await p.changeUser(uid);
      return { purchases: p, mod };
    }
    return { purchases: P.configure({ apiKey: RC_WEB_KEY, appUserId: uid }), mod };
  });
  sdk.catch(() => (sdk = null));
  return sdk;
}

export interface WebPlans {
  monthly: Package | null;
  annual: Package | null;
  /** the school packs (annual): buying one makes a school licence its buyer manages (functions/src/schools.ts) */
  teams: Package | null;
  schools: Package | null;
}

/**
 * The web plans: the monthly and annual packages of the offering and its school packs (packages `teams`,
 * 5 teachers, and `schools`, 20), in the currency RevenueCat picks for the customer's country (or the one
 * asked for with `?currency=`, ui/localPrice.ts).
 */
export async function webPlans(uid: string, currency = CURRENCY_OVERRIDE): Promise<WebPlans> {
  const { purchases } = await revenueCat(uid);
  const offerings = await purchases.getOfferings(currency ? { currency } : undefined);
  const offering: Offering | null = (RC_OFFERING ? offerings.all[RC_OFFERING] : null) ?? offerings.current;
  return { monthly: offering?.monthly ?? null, annual: offering?.annual ?? null, teams: offering?.packagesById.teams ?? null, schools: offering?.packagesById.schools ?? null };
}

/**
 * Buys a plan with RevenueCat's checkout. Resolves true when bought, false
 * when the customer closed it.
 */
export async function buy(uid: string, pkg: Package, opts: { email?: string | null; locale: string }): Promise<boolean> {
  const { purchases, mod } = await revenueCat(uid);
  try {
    await purchases.purchase({ rcPackage: pkg, customerEmail: opts.email ?? undefined, selectedLocale: opts.locale, defaultLocale: "en" });
    return true;
  } catch (err) {
    if (err instanceof mod.PurchasesError && err.errorCode === mod.ErrorCode.UserCancelledError) return false;
    throw err;
  }
}

/** Where a web subscription is managed (RevenueCat's customer portal), or the store's. */
export async function managementUrl(uid: string): Promise<string | null> {
  const { purchases } = await revenueCat(uid);
  return (await purchases.getCustomerInfo()).managementURL;
}
